import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	IconChevronRight,
	IconChartBar,
	IconCoin,
	IconSettings,
	IconLogout,
} from "@tabler/icons-react";
import { useAuth } from "@/hooks/useAuth";

interface AdminMenuSheetProps {
	open: boolean;
	onClose: () => void;
}

interface MenuItem {
	icon: React.ReactNode;
	iconBg: string;
	iconColor: string;
	title: string;
	description: string;
	to?: string;
	action?: () => void;
}

export function AdminMenuSheet({ open, onClose }: AdminMenuSheetProps) {
	const navigate = useNavigate();
	const { signOut } = useAuth();
	const [visible, setVisible] = useState(false);
	const [animating, setAnimating] = useState(false);
	const sheetRef = useRef<HTMLDivElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);

	const MENU_ITEMS: MenuItem[] = [
		{
			icon: <IconChartBar size={20} />,
			iconBg: "color-mix(in srgb, #f97316 12%, var(--color-surface))",
			iconColor: "#f97316",
			title: "Tracking",
			description: "Lihat data tracking pengguna",
			to: "/manage/tracking",
		},
		{
			icon: <IconCoin size={20} />,
			iconBg: "color-mix(in srgb, #10b981 12%, var(--color-surface))",
			iconColor: "#10b981",
			title: "Affiliate",
			description: "Kelola sistem referral & komisi",
			to: "/manage/affiliate",
		},
		{
			icon: <IconSettings size={20} />,
			iconBg: "color-mix(in srgb, #6b7280 12%, var(--color-surface))",
			iconColor: "#6b7280",
			title: "Settings",
			description: "Pengaturan aplikasi",
			to: "/manage/settings",
		},
		{
			icon: <IconLogout size={20} />,
			iconBg: "color-mix(in srgb, #ef4444 12%, var(--color-surface))",
			iconColor: "#ef4444",
			title: "Keluar",
			description: "Logout dari admin",
			action: () => signOut(),
		},
	];

	useEffect(() => {
		if (open) {
			previousFocusRef.current = document.activeElement as HTMLElement;
			window.history.pushState("", "");
			const handlePopState = () => {
				setAnimating(false);
				setTimeout(onClose, 280);
			};
			window.addEventListener("popstate", handlePopState);
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setVisible(true);
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setAnimating(true);
					sheetRef.current?.focus();
				});
			});
			return () => window.removeEventListener("popstate", handlePopState);
		} else {
			setAnimating(false);
			const t = setTimeout(() => {
				setVisible(false);
				previousFocusRef.current?.focus();
			}, 300);
			return () => clearTimeout(t);
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === "Escape") {
				setAnimating(false);
				setTimeout(onClose, 280);
			}
		}
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open, onClose]);

	if (!visible) return null;

	function handleItemClick(item: MenuItem) {
		setAnimating(false);
		setTimeout(() => {
			if (item.action) {
				item.action();
			} else if (item.to) {
				navigate(item.to);
			}
			onClose();
		}, 280);
	}

	return (
		<div
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 9999,
				display: "flex",
				alignItems: "flex-end",
				justifyContent: "center",
				pointerEvents: visible ? "auto" : "none",
			}}
		>
			<div
				style={{
					position: "absolute",
					inset: 0,
					background: "rgba(0,0,0,0)",
					transition: "background 300ms ease",
					...(animating ? { background: "rgba(0,0,0,0.35)" } : {}),
				}}
				onClick={() => {
					setAnimating(false);
					setTimeout(onClose, 280);
				}}
			/>

			<div
				ref={sheetRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="admin-menu-dialog"
				tabIndex={-1}
				style={{
					position: "relative",
					width: "100%",
					maxWidth: 480,
					background: "var(--color-surface)",
					borderRadius: "var(--radius-xl) var(--radius-xl) 0 0",
					boxShadow: "0 -4px 24px rgba(0,0,0,0.12)",
					transform: animating ? "translateY(0)" : "translateY(100%)",
					transition: "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)",
					paddingBottom: "env(safe-area-inset-bottom, 0px)",
					maxHeight: "70dvh",
					overflow: "auto",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						padding: "0.75rem 0 0.25rem",
					}}
				>
					<div
						style={{
							width: 36,
							height: 4,
							borderRadius: 999,
							background: "var(--color-border)",
						}}
					/>
				</div>

				<div style={{ padding: "0.5rem 1.25rem 1rem" }}>
					<h3
						id="admin-menu-dialog"
						style={{
							margin: "0 0 0.75rem",
							fontSize: "0.95rem",
							fontWeight: 700,
							color: "var(--color-text)",
						}}
					>
						Menu Admin
					</h3>

					<div style={{ display: "grid", gap: "0.4rem" }}>
						{MENU_ITEMS.map((item) => (
							<button
								key={item.title}
								type="button"
								onClick={() => handleItemClick(item)}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.85rem",
									padding: "0.75rem",
									borderRadius: "var(--radius-md)",
									border: "none",
									background: "transparent",
									cursor: "pointer",
									textAlign: "left",
									width: "100%",
									transition: "background 150ms ease",
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.background =
										"var(--color-surface-2)")
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.background = "transparent")
								}
							>
								<div
									style={{
										width: 40,
										height: 40,
										borderRadius: "var(--radius-md)",
										background: item.iconBg,
										color: item.iconColor,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
									}}
								>
									{item.icon}
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div
										style={{
											fontSize: "0.88rem",
											fontWeight: 600,
											color: "var(--color-text)",
											lineHeight: 1.3,
										}}
									>
										{item.title}
									</div>
									<div
										style={{
											fontSize: "0.78rem",
											color: "var(--color-muted)",
											lineHeight: 1.3,
											marginTop: 2,
										}}
									>
										{item.description}
									</div>
								</div>
								<IconChevronRight
									size={16}
									style={{
										color: "var(--color-muted)",
										flexShrink: 0,
									}}
								/>
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
